import { lazy, Suspense } from 'react';
import { Routes, Route, useParams } from 'react-router';
import { Providers } from '@/components/providers';
import { ThemeScript } from '@/components/theme-script';
import { LoadingPage } from '@/components/LoadingPage';
import { ProtectedPage } from '@/components/ProtectedPage';
import { MainLayout } from '@/components/Layout/MainLayout';
import { SuperAdminRoute } from '@/modules/auth/components/SuperAdminRoute';
import { SuperAdminLayout } from '@/components/Layout/SuperAdminLayout';
import { PublicTournamentDetail } from '@/modules/tournaments/pages/PublicTournamentDetail';
import { PublicMerchStore } from '@/modules/merch/pages/PublicMerchStore';
import { MerchProductDetailPage } from '@/modules/merch/pages/MerchProductDetailPage';
import { ValorantMatchDetailPage } from '@/modules/valorant/pages/ValorantMatchDetailPage';
import { PublicOrgSubscriptions } from '@/modules/subscriptions/pages/PublicOrgSubscriptions';

// Wrapper components that extract params
function PublicTournamentDetailWrapper() {
  const { slug } = useParams<{ slug: string }>();
  return <PublicTournamentDetail slug={slug!} />;
}

function PublicMerchStoreWrapper() {
  const { slug } = useParams<{ slug: string }>();
  return <PublicMerchStore slug={slug!} />;
}

function MerchProductDetailWrapper() {
  const { slug, productId } = useParams<{ slug: string; productId: string }>();
  return <MerchProductDetailPage slug={slug!} productId={productId!} />;
}

function PublicOrgSubscriptionsWrapper() {
  const { slug } = useParams<{ slug: string }>();
  return <PublicOrgSubscriptions slug={slug!} />;
}

function ValorantMatchDetailWrapper() {
  const { matchId } = useParams<{ matchId: string }>();
  return (
    <ProtectedPage>
      <MainLayout>
        <ValorantMatchDetailPage matchId={matchId!} />
      </MainLayout>
    </ProtectedPage>
  );
}


const Home = lazy(() => import('@/modules/home/pages/Home').then(m => ({ default: m.Home })));
const SignInPage = lazy(() => import('@/modules/auth/components/Login').then(m => ({ default: m.SignInPage })));
const AuthCallback = lazy(() => import('@/modules/auth/components/AuthCallback').then(m => ({ default: m.AuthCallback })));
const AuthDiscordCallback = lazy(() => import('@/modules/auth/components/AuthDiscordCallback').then(m => ({ default: m.AuthDiscordCallback })));
const Dashboard = lazy(() => import('@/modules/dashboard/pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Tournaments = lazy(() => import('@/modules/tournaments/pages/Tournaments'));
const TournamentDetail = lazy(() => import('@/modules/tournaments/pages/TournamentDetail'));
const PublicTournamentList = lazy(() => import('@/modules/tournaments/pages/PublicTournamentList').then(m => ({ default: m.PublicTournamentList })));
const TournamentPreview = lazy(() => import('@/modules/tournaments/pages/TournamentPreview').then(m => ({ default: m.TournamentPreview })));
const ValorantHome = lazy(() => import('@/modules/valorant/pages/ValorantHome').then(m => ({ default: m.ValorantHome })));
const PlayerMatches = lazy(() => import('@/modules/valorant/pages/PlayerMatches').then(m => ({ default: m.PlayerMatches })));
const LeaderboardPage = lazy(() => import('@/modules/valorant/pages/LeaderboardPage').then(m => ({ default: m.LeaderboardPage })));
const PremierPage = lazy(() => import('@/modules/valorant/pages/PremierPage').then(m => ({ default: m.PremierPage })));
const VodsPage = lazy(() => import('@/modules/vods/VodsPage').then(m => ({ default: m.VodsPage })));
const Scrims = lazy(() => import('@/modules/scrims/pages/Scrims').then(m => ({ default: m.Scrims })));
const OverlaysHome = lazy(() => import('@/modules/overlays/pages/OverlaysHome').then(m => ({ default: m.OverlaysHome })));
const ValorantOverlayDetail = lazy(() => import('@/modules/overlays/pages/ValorantOverlayDetail').then(m => ({ default: m.ValorantOverlayDetail })));
const ValorantOverlayView = lazy(() => import('@/modules/overlays/pages/ValorantOverlayView').then(m => ({ default: m.ValorantOverlayView })));
const CarouselOverlayDetail = lazy(() => import('@/modules/overlays/pages/CarouselOverlayDetail').then(m => ({ default: m.CarouselOverlayDetail })));
const CarouselOverlayView = lazy(() => import('@/modules/overlays/pages/CarouselOverlayView').then(m => ({ default: m.CarouselOverlayView })));
const StreamAlertOverlayView = lazy(() => import('@/modules/overlays/pages/StreamAlertOverlayView').then(m => ({ default: m.StreamAlertOverlayView })));
const ControlsHub = lazy(() => import('@/modules/controls/pages/ControlsHub').then(m => ({ default: m.ControlsHub })));
const OrganizationSettings = lazy(() => import('@/modules/controls/pages/OrganizationSettings').then(m => ({ default: m.OrganizationSettings })));
const Rosters = lazy(() => import('@/modules/controls/pages/Rosters').then(m => ({ default: m.Rosters })));
const ClientsPage = lazy(() => import('@/modules/controls/pages/Clients').then(m => ({ default: m.Clients })));
const Creators = lazy(() => import('@/modules/controls/pages/Creators').then(m => ({ default: m.Creators })));
const ControlDiscordIntegration = lazy(() => import('@/modules/controls/pages/DiscordIntegration').then(m => ({ default: m.DiscordIntegration })));
const IntegrationsHub = lazy(() => import('@/modules/integrations/pages/IntegrationsHub').then(m => ({ default: m.IntegrationsHub })));
const DiscordIntegrationPage = lazy(() => import('@/modules/controls/pages/DiscordIntegration').then(m => ({ default: m.DiscordIntegration })));
const KickIntegrationPage = lazy(() => import('@/modules/integrations/kick/KickIntegrationPage').then(m => ({ default: m.KickIntegrationPage })));
const MerchManagement = lazy(() => import('@/modules/merch/pages/MerchManagement').then(m => ({ default: m.MerchManagement })));
const ValorantPerformance = lazy(() => import('@/modules/valorant/pages/ValorantPerformance').then(m => ({ default: m.ValorantPerformance })));
const SubscriptionDetailPage = lazy(() => import('@/modules/subscriptions/pages/SubscriptionDetailPage').then(m => ({ default: m.SubscriptionDetailPage })));
const SubscriptionSuccessPage = lazy(() => import('@/modules/subscriptions/pages/SubscriptionSuccessPage').then(m => ({ default: m.SubscriptionSuccessPage })));
const SubscriptionFailurePage = lazy(() => import('@/modules/subscriptions/pages/SubscriptionFailurePage').then(m => ({ default: m.SubscriptionFailurePage })));
const SettingsPage = lazy(() => import('@/modules/settings/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const ProductsHome = lazy(() => import('@/modules/products/pages/ProductsHome').then(m => ({ default: m.ProductsHome })));
const CategoriesHome = lazy(() => import('@/modules/products/pages/CategoriesHome').then(m => ({ default: m.CategoriesHome })));
const QuotesHome = lazy(() => import('@/modules/quote/pages/QuotesHome').then(m => ({ default: m.QuotesHome })));
const QrGeneratorHome = lazy(() => import('@/modules/qr-generator/pages/QrGeneratorHome').then(m => ({ default: m.QrGeneratorHome })));
const ObsControlHome = lazy(() => import('@/modules/multimedia/obs-control/pages/ObsControlHome').then(m => ({ default: m.ObsControlHome })));
const PublicActivitiesHome = lazy(() => import('@/modules/activities/pages/PublicActivitiesHome').then(m => ({ default: m.PublicActivitiesHome })));
const StopSignalTest = lazy(() => import('@/modules/activities/pages/StopSignalTest').then(m => ({ default: m.StopSignalTest })));
const TrailMakingTest = lazy(() => import('@/modules/activities/pages/TrailMakingTest').then(m => ({ default: m.TrailMakingTest })));
const ThemePreview = lazy(() => import('@/modules/design/pages/ThemePreviewPage').then(m => ({ default: m.ThemePreviewPage })));
const PlatformOverview = lazy(() => import('@/modules/super-admin/pages/PlatformOverview').then(m => ({ default: m.PlatformOverview })));
const PlatformAdminsPage = lazy(() => import('@/modules/super-admin/pages/SuperAdminsPage').then(m => ({ default: m.SuperAdminsPage })));
const PlatformUsersPage = lazy(() => import('@/modules/super-admin/pages/PlatformUsersPage').then(m => ({ default: m.PlatformUsersPage })));
const PlatformUserDetailPage = lazy(() => import('@/modules/super-admin/pages/PlatformUserDetailPage').then(m => ({ default: m.PlatformUserDetailPage })));
const OrganizationsPage = lazy(() => import('@/modules/super-admin/pages/OrganizationsPage').then(m => ({ default: m.OrganizationsPage })));
const OrganizationDetailPage = lazy(() => import('@/modules/super-admin/pages/OrganizationDetailPage').then(m => ({ default: m.OrganizationDetailPage })));
const GamesPage = lazy(() => import('@/modules/super-admin/pages/GamesManagement').then(m => ({ default: m.GamesManagement })));
const NavigationConfigPage = lazy(() => import('@/modules/super-admin/pages/MenuManagement').then(m => ({ default: m.MenuManagement })));
const AiAssistantPage = lazy(() => import('@/modules/super-admin/pages/AiAdmin').then(m => ({ default: m.AiAdmin })));
const ActivitiesAdminHub = lazy(() => import('@/modules/super-admin/pages/ActivitiesAdminHub').then(m => ({ default: m.ActivitiesAdminHub })));
const ActivitiesCatalog = lazy(() => import('@/modules/super-admin/pages/ActivitiesCatalogAdmin').then(m => ({ default: m.ActivitiesCatalogAdmin })));
const ActivitiesResults = lazy(() => import('@/modules/super-admin/pages/ActivitiesResultsAdmin').then(m => ({ default: m.ActivitiesResultsAdmin })));
const SuperAdminStopSignal = lazy(() => import('@/modules/super-admin/pages/StopSignalAdmin').then(m => ({ default: m.StopSignalAdmin })));
const SuperAdminTrailMaking = lazy(() => import('@/modules/super-admin/pages/TrailMakingAdmin').then(m => ({ default: m.TrailMakingAdmin })));
const NotFound = lazy(() => import('@/components/NotFound').then(m => ({ default: m.NotFound })));
const MerchSuccessPage = lazy(() => import('@/modules/merch/pages/MerchSuccessPage').then(m => ({ default: m.MerchSuccessPage })));
const MerchFailurePage = lazy(() => import('@/modules/merch/pages/MerchFailurePage').then(m => ({ default: m.MerchFailurePage })));

function App() {
  return (
    <Providers>
      <ThemeScript />
      <Suspense fallback={<LoadingPage />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<SignInPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/discord/callback" element={<AuthDiscordCallback />} />
          
          {/* Public tournaments */}
          <Route path="/tournaments" element={<PublicTournamentList />} />
          <Route path="/tournaments/:slug" element={<PublicTournamentDetailWrapper />} />
          <Route path="/tournaments/:slug/preview" element={<TournamentPreview />} />
          
          {/* Public merch */}
          <Route path="/merch/:slug" element={<PublicMerchStoreWrapper />} />
          <Route path="/merch/:slug/:productId" element={<MerchProductDetailWrapper />} />
          <Route path="/merch/success" element={<MerchSuccessPage />} />
          <Route path="/merch/failure" element={<MerchFailurePage />} />
          
          {/* Public subscriptions */}
          <Route path="/subscriptions" element={<PublicOrgSubscriptionsWrapper />} />
          <Route path="/subscriptions/:slug" element={<SubscriptionDetailPage />} />
          <Route path="/subscriptions/:slug/success" element={<SubscriptionSuccessPage />} />
          <Route path="/subscriptions/:slug/failure" element={<SubscriptionFailurePage />} />
          <Route path="/subscriptions/success" element={<SubscriptionSuccessPage />} />
          <Route path="/subscriptions/failure" element={<SubscriptionFailurePage />} />
          
          {/* Public activities */}
          <Route path="/activities" element={<PublicActivitiesHome />} />
          <Route path="/activities/stop-signal" element={<StopSignalTest />} />
          <Route path="/activities/trail-making" element={<TrailMakingTest />} />
          
          {/* Public overlays */}
          <Route path="/overlays/valorant/view" element={<ValorantOverlayView />} />
          <Route path="/overlays/valorant/:id/view" element={<ValorantOverlayView />} />
          <Route path="/overlays/carousel/:id/view" element={<CarouselOverlayView />} />
          <Route path="/overlays/alerts/:id/view" element={<StreamAlertOverlayView />} />
          
          {/* Theme preview */}
          <Route path="/theme-preview" element={<ThemePreview />} />
          
          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedPage>
                <MainLayout>
                  <Dashboard />
                </MainLayout>
              </ProtectedPage>
            }
          />
          
          {/* Tournaments admin */}
          <Route
            path="/tournaments/admin"
            element={
              <ProtectedPage>
                <MainLayout>
                  <Tournaments />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/tournaments/admin/:id"
            element={
              <ProtectedPage>
                <MainLayout>
                  <TournamentDetail />
                </MainLayout>
              </ProtectedPage>
            }
          />
          
          {/* Valorant */}
          <Route
            path="/valorant"
            element={
              <ProtectedPage>
                <MainLayout>
                  <ValorantHome />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/valorant/performance"
            element={
              <ProtectedPage>
                <MainLayout>
                  <ValorantPerformance />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/valorant/performance/matches/:matchId"
            element={<ValorantMatchDetailWrapper />}
          />
          <Route
            path="/valorant/player/:region/:name/:tag"
            element={
              <ProtectedPage>
                <MainLayout>
                  <PlayerMatches />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/valorant/leaderboard"
            element={
              <ProtectedPage>
                <MainLayout>
                  <LeaderboardPage />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/valorant/premier"
            element={
              <ProtectedPage>
                <MainLayout>
                  <PremierPage />
                </MainLayout>
              </ProtectedPage>
            }
          />
          
          {/* Vods */}
          <Route
            path="/vods"
            element={
              <ProtectedPage>
                <MainLayout>
                  <VodsPage />
                </MainLayout>
              </ProtectedPage>
            }
          />
          
          {/* Scrims */}
          <Route
            path="/scrims"
            element={
              <ProtectedPage>
                <MainLayout>
                  <Scrims />
                </MainLayout>
              </ProtectedPage>
            }
          />
          
          {/* Overlays */}
          <Route
            path="/overlays"
            element={
              <ProtectedPage>
                <MainLayout>
                  <OverlaysHome />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/overlays/valorant/:id"
            element={
              <ProtectedPage>
                <MainLayout>
                  <ValorantOverlayDetail />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/overlays/carousel/:id"
            element={
              <ProtectedPage>
                <MainLayout>
                  <CarouselOverlayDetail />
                </MainLayout>
              </ProtectedPage>
            }
          />
          
          {/* Control */}
          <Route
            path="/control"
            element={
              <ProtectedPage>
                <MainLayout>
                  <ControlsHub />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/control/organization"
            element={
              <ProtectedPage>
                <MainLayout>
                  <OrganizationSettings />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/control/rosters"
            element={
              <ProtectedPage>
                <MainLayout>
                  <Rosters />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/control/clients"
            element={
              <ProtectedPage>
                <MainLayout>
                  <ClientsPage />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/control/creators"
            element={
              <ProtectedPage>
                <MainLayout>
                  <Creators />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/control/discord-integration"
            element={
              <ProtectedPage>
                <MainLayout>
                  <ControlDiscordIntegration />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/control/merch"
            element={
              <ProtectedPage>
                <MainLayout>
                  <MerchManagement />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/control/subscriptions"
            element={
              <ProtectedPage>
                <MainLayout>
                  <PublicOrgSubscriptionsWrapper />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/control/subscriptions/:id"
            element={
              <ProtectedPage>
                <MainLayout>
                  <SubscriptionDetailPage />
                </MainLayout>
              </ProtectedPage>
            }
          />
          
          {/* Integrations */}
          <Route
            path="/integrations"
            element={
              <ProtectedPage>
                <MainLayout>
                  <IntegrationsHub />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/integrations/discord"
            element={
              <ProtectedPage>
                <MainLayout>
                  <DiscordIntegrationPage />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/integrations/kick"
            element={
              <ProtectedPage>
                <MainLayout>
                  <KickIntegrationPage />
                </MainLayout>
              </ProtectedPage>
            }
          />
          
          {/* Merch admin - removed, use control/merch instead */}
          
          {/* Subscriptions admin - removed, use control/subscriptions instead */}
          
          {/* Settings */}
          <Route
            path="/settings"
            element={
              <ProtectedPage>
                <MainLayout>
                  <SettingsPage />
                </MainLayout>
              </ProtectedPage>
            }
          />
          
          {/* Products */}
          <Route
            path="/products"
            element={
              <ProtectedPage>
                <MainLayout>
                  <ProductsHome />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/products/new"
            element={
              <ProtectedPage>
                <MainLayout>
                  <ProductsHome />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/products/edit/:id"
            element={
              <ProtectedPage>
                <MainLayout>
                  <ProductsHome />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/products/categories"
            element={
              <ProtectedPage>
                <MainLayout>
                  <CategoriesHome />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/products/categories/new"
            element={
              <ProtectedPage>
                <MainLayout>
                  <CategoriesHome />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/products/categories/edit/:id"
            element={
              <ProtectedPage>
                <MainLayout>
                  <CategoriesHome />
                </MainLayout>
              </ProtectedPage>
            }
          />
          
          {/* Quotes */}
          <Route
            path="/quotes"
            element={
              <ProtectedPage>
                <MainLayout>
                  <QuotesHome />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/quotes/new"
            element={
              <ProtectedPage>
                <MainLayout>
                  <QuotesHome />
                </MainLayout>
              </ProtectedPage>
            }
          />
          <Route
            path="/quotes/edit/:id"
            element={
              <ProtectedPage>
                <MainLayout>
                  <QuotesHome />
                </MainLayout>
              </ProtectedPage>
            }
          />
          
          {/* QR Generator */}
          <Route
            path="/qr"
            element={
              <ProtectedPage>
                <MainLayout>
                  <QrGeneratorHome />
                </MainLayout>
              </ProtectedPage>
            }
          />
          
          {/* Multimedia */}
          <Route
            path="/multimedia/obs-control"
            element={
              <ProtectedPage>
                <MainLayout>
                  <ObsControlHome />
                </MainLayout>
              </ProtectedPage>
            }
          />
          
          {/* Super Admin routes */}
          <Route
            path="/super-admin"
            element={
              <SuperAdminRoute>
                <SuperAdminLayout>
                  <PlatformOverview />
                </SuperAdminLayout>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/admins"
            element={
              <SuperAdminRoute>
                <SuperAdminLayout>
                  <PlatformAdminsPage />
                </SuperAdminLayout>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/users"
            element={
              <SuperAdminRoute>
                <SuperAdminLayout>
                  <PlatformUsersPage />
                </SuperAdminLayout>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/users/:userId"
            element={
              <SuperAdminRoute>
                <SuperAdminLayout>
                  <PlatformUserDetailPage />
                </SuperAdminLayout>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/organizations"
            element={
              <SuperAdminRoute>
                <SuperAdminLayout>
                  <OrganizationsPage />
                </SuperAdminLayout>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/organizations/:orgId"
            element={
              <SuperAdminRoute>
                <SuperAdminLayout>
                  <OrganizationDetailPage />
                </SuperAdminLayout>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/games"
            element={
              <SuperAdminRoute>
                <SuperAdminLayout>
                  <GamesPage />
                </SuperAdminLayout>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/navigation"
            element={
              <SuperAdminRoute>
                <SuperAdminLayout>
                  <NavigationConfigPage />
                </SuperAdminLayout>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/ai"
            element={
              <SuperAdminRoute>
                <SuperAdminLayout>
                  <AiAssistantPage />
                </SuperAdminLayout>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/activities"
            element={
              <SuperAdminRoute>
                <SuperAdminLayout>
                  <ActivitiesAdminHub />
                </SuperAdminLayout>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/activities/catalog"
            element={
              <SuperAdminRoute>
                <SuperAdminLayout>
                  <ActivitiesCatalog />
                </SuperAdminLayout>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/activities/results"
            element={
              <SuperAdminRoute>
                <SuperAdminLayout>
                  <ActivitiesResults />
                </SuperAdminLayout>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/activities/stop-signal"
            element={
              <SuperAdminRoute>
                <SuperAdminLayout>
                  <SuperAdminStopSignal />
                </SuperAdminLayout>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/activities/trail-making"
            element={
              <SuperAdminRoute>
                <SuperAdminLayout>
                  <SuperAdminTrailMaking />
                </SuperAdminLayout>
              </SuperAdminRoute>
            }
          />
          
          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Providers>
  );
}

export default App;
